/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import Swiper from 'swiper';
import _ from 'lodash';

import domUtil from 'scripts/utils/dom';
import dtm from 'scripts/services/dtm';
import appContext from 'scripts/app-context';
import deviceUtil from 'scripts/utils/device';
import {
    WIN_PROBABILITY,
    MAKE_CUT_PROBABILITY,
    getFieldName as getProbabilityFieldName,
} from 'components/probability/types';
import { hideTooltip } from 'components/probability/tooltipHelpers';

import { prepareChartData, isChartDataEqual } from '../../probability/computes';

import template from './video-recap.stache';
import { PROBABILITY_PAGE_TYPE } from 'components/probability/tooltip-handler';
import { getVideos } from './computes/get-videos';
import { sendChartAnalytics } from 'components/probability/controllers/analytics.controller';

const youboraOptions = _.get(window.globalSettings, 'youbora', {});

can.Component.extend({
    tag: 'video-recap',
    template: template,
    leakScope: false,
    viewModel: {
        init() {
            this.bind('probabilities', () => {
                this.updateChartData();
            });
            this.bind('probabilityType', () => {
                this.updateChartData();
            });

            this.updateChartData();
        },
        define: {
            player: {
                Type: can.Map,
                value: null,
            },
            activeVideo: {
                Type: can.Map,
                value: null,
                set(video) {
                    return video.identifier ? video : null;
                },
            },
            videos: {
                type: 'array',
                value: [],
            },
            pageType: {
                type: 'string',
                value: PROBABILITY_PAGE_TYPE.PLAYER_DETAIL,
            },
            activeVideoData: {
                get() {
                    const activeVideo = this.attr('activeVideo');
                    if (activeVideo) {
                        return this.getVideoData(activeVideo.identifier);
                    }
                    return null;
                },
            },
            youboraOptions: {
                Type: can.Map,
                value: '*',
            },
            probabilities: {
                set(value) {
                    const day = _.get(value, 'dayNumber');

                    if (!this.attr('probabilityType')) {
                        this.attr(
                            'probabilityType',
                            day > 2 ? WIN_PROBABILITY : MAKE_CUT_PROBABILITY,
                        );
                    }

                    return value;
                },
            },
            probabilityType: {
                type: 'string',
            },
            probability: {
                type: 'object',
            },
            activeTooltipId: {
                type: 'string',
            },
            chartsData: {
                type: 'array',
            },
            yAxis: {
                type: 'array',
            },
            isSwiperBeginning: {
                type: 'boolean',
                value: true,
            },
            isSwiperEnd: {
                type: 'boolean',
                value: false,
            },
        },

        getVideoData(identifier, round, hole) {
            const videos = this.attr('videos') || [];
            if (videos.length && identifier === 'start') {
                return videos[0];
            }

            let videoKey = null;
            videos.forEach((item, key) => {
                if (item.video.identifier === identifier) {
                    videoKey = key;
                }
            });

            if (videoKey === null && round && hole) {
                videos.forEach((item, key) => {
                    if (item.round === +round && item.hole === +hole) {
                        videoKey = key;
                    }
                });
            }

            if (videoKey !== null) {
                return videos[videoKey];
            }

            return null;
        },

        playNextVideo() {
            const activeVideoData = this.attr('activeVideoData');
            if (activeVideoData) {
                const videos = this.attr('videos');
                let activeVideoKey = activeVideoData.key;
                if (activeVideoKey !== null && videos[activeVideoKey + 1]) {
                    this.attr('activeVideo', videos[activeVideoKey + 1].video);
                }
            }
        },

        startVideoRecap() {
            const startVideo = (this.attr('videos') || [])[0];
            if (startVideo) {
                this.attr('activeVideo', startVideo.video);
            }
        },

        stopVideo() {
            this.removeAttr('activeVideo');
        },

        hideTooltip() {
            const tooltip = this.attr('tooltip');
            hideTooltip(tooltip);
        },

        updateChartData() {
            const probabilityType = this.attr('probabilityType');
            const probabilityField = getProbabilityFieldName(probabilityType);
            const probabilities = this.attr('probabilities');
            const probability = _.get(probabilities, probabilityField);
            this.attr('probability', probability);

            const chartsData = this.attr('chartsData');
            const preparedData = prepareChartData(
                probability.serialize(),
                null,
                false,
            );

            if (
                !chartsData ||
                !isChartDataEqual(preparedData, chartsData.serialize())
            ) {
                this.attr('chartsData', preparedData);
                this.attr('yAxis', preparedData[0].yAxis);
                this.updateVideos();
            }
        },

        updateVideos() {
            const chartsData = this.attr('chartsData').serialize();
            const videos = getVideos(chartsData);

            this.attr('videos', videos);
        },
    },
    events: {
        inserted() {
            this.viewModel.attr('tooltip', this.element.find('.c-tooltip'));
            this.initSwiper();
            this.registerRoutes();
            this.registerModelListeners();
        },

        registerRoutes() {
            can.route('section=:section&video=:video&r=:r&h=:h');
            can.route.ready();
        },

        registerModelListeners() {
            appContext.model.screen.bind('screenSize', () => this.initSwiper());

            this.viewModel.bind('probabilities', (_ev, newVal, oldVal) => {
                if (newVal && oldVal) {
                    if (
                        JSON.stringify(newVal.serialize()) !==
                        JSON.stringify(oldVal.serialize())
                    ) {
                        setTimeout(() => this.updateSwiper(), 10);
                    }
                }
            });

            this.viewModel.bind('probabilityType', () => this.updateSwiper());

            this.viewModel.bind('activeVideo', (ev, value) => {
                if (!value) {
                    can.route.removeAttr('video');
                    can.route.removeAttr('r');
                    can.route.removeAttr('h');
                    this.viewModel.attr('youboraOptions', null);
                    return;
                }

                const videoData = this.viewModel.getVideoData(value.identifier);
                can.route.attr({
                    video: value.identifier,
                    r: videoData.round,
                    h: videoData.hole,
                });

                domUtil.scrollTop(0);
                this.swipeTo(videoData);

                const player = this.viewModel.attr('player');

                if (player && deviceUtil.isMobile) {
                    this.viewModel.attr(
                        'youboraOptions',
                        this.getYouboraOptions(player),
                    );
                }
            });

            appContext.model.on('highlights-video-going-play', (ev, video) => {
                dtm.trackVideoLaunch(video.title, 'Player Detail Scorecard');
                this.viewModel.attr('activeVideo', video);
            });

            //handle render chart data on first init
            appContext.model.one('chart-data-rendered', () => {
                const videoId = can.route.attr('video');
                const round = can.route.attr('r');
                const hole = can.route.attr('h');
                const videoData = this.viewModel.getVideoData(
                    videoId,
                    round,
                    hole,
                );

                if (videoData) {
                    this.viewModel.attr('activeVideo', videoData.video);
                }
            });
        },

        swipeTo(videoData) {
            if (!this.swiper || !videoData) {
                return;
            }

            const currentSwiperTranslate = this.swiper.getTranslate();
            const slideIndex = videoData.round - 1;
            const videoId = videoData.video.identifier;
            const swiperOffsetLeft = this.element
                .find('.swiper-container')
                .offset().left;
            const swiperOffsetWidth = this.element
                .find('.swiper-container')
                .width();
            const activeVideoButton = this.element
                .find('probability-round')
                .eq(slideIndex)
                .find(`.s-label.has-video[data-identifier=${videoId}]`);

            if (!activeVideoButton.length) {
                return;
            }

            const translate =
                currentSwiperTranslate -
                (activeVideoButton.offset().left -
                    (swiperOffsetWidth / 2 + swiperOffsetLeft));

            this.swiper.translateTo(translate);
        },

        removed() {
            this.viewModel.stopVideo();
        },

        initSwiper() {
            if (this.swiper) {
                this.swiper.destroy(true);
                this.swiper = null;
            }

            const $swiperContainer = this.element.find('.swiper-container');
            const isContainerExists = Boolean($swiperContainer.length);

            if (!isContainerExists) {
                return;
            }

            this.swiper = new Swiper($swiperContainer, {
                simulateTouch: true,
                slidesPerView: 'auto',
                freeMode: true,
                scrollbar: {
                    el: this.element.find('.swiper-scrollbar'),
                    dragSize: 28,
                    draggable: true,
                    snapOnRelease: false,
                },
                on: {
                    sliderMove: () =>
                        // dispatch video recap slider move event
                        appContext.model.dispatch('video-recap-slider-move'),
                    progress: (progress) => {
                        this.viewModel.attr('isSwiperEnd', progress >= 1);
                        this.viewModel.attr('isSwiperBeginning', progress <= 0);
                        this.viewModel.hideTooltip();
                    },
                },
            });
        },

        updateSwiper() {
            if (!this.swiper) {
                return;
            }

            this.swiper.update();
            this.swiper.scrollbar.updateSize();
        },

        getYouboraOptions(player) {
            if (!player) {
                return null;
            }

            const playerFullName = player
                ? `${player.firstName} ${player.lastName}`
                : null;

            return {
                accountCode: youboraOptions.accountCode,
                transactionCode: `Video recap for ${playerFullName} on ${new Date().toISOString()}`,
                media: {
                    title: this.viewModel.attr('activeVideo.identifier'),
                    isLive: false,
                },
                extraParams: {
                    param1: playerFullName,
                    param2: 'Mobile Player Detail',
                },
            };
        },
        '.s-graph-dot mouseover'() {
            const value = this.viewModel.attr('activeTooltipId');

            sendChartAnalytics(
                {
                    type: this.viewModel.attr('probabilityType'),
                    player: this.viewModel.attr('player'),
                    pageType: this.viewModel.attr('pageType'),
                },
                value,
            );
        },
    },
});
